/**
 * Émettre une note de crédit — l'écran qui pose vraiment la question.
 *
 * ## Le défaut dont cet écran est la correction
 *
 * L'ancien formulaire portait une case « Annuler les inscriptions associées »
 * **cochée par défaut**. Le constat de l'administration : « ce qui arrive
 * souvent c'est que l'utilisateur oublie de la décocher ». Le résultat était
 * une inscription annulée alors que l'enfant venait au stage, et un compteur
 * de places faux.
 *
 * Décocher la case par défaut n'aurait rien réglé : une case, cochée ou non,
 * ne pose pas de question. Ici, **deux boutons exclusifs, aucun présélectionné**,
 * et « Créer la note de crédit » reste inactif jusqu'à ce que l'un des deux
 * soit choisi. L'écran ne peut pas être validé par inadvertance.
 *
 * ## Le mode qui manquait
 *
 * « La plupart du temps les notes de crédit sont faites parce que les parents
 * voulaient le tarif réduit mais ont oublié de cocher la case ». L'ancien écran
 * n'avait pas de mode pour ça : on passait par « montant personnalisé », et
 * l'inscription restait au tarif normal — donc le compte des inscriptions à
 * tarif réduit était faux, définitivement.
 *
 * Le mode **Correction de tarif** propose les tarifs réels de la session,
 * calcule l'écart depuis le montant facturé, et requalifie l'inscription.
 * L'annulation n'y est pas proposée : l'enfant vient au stage.
 *
 * ## Rien n'est écrit sans que le plan soit montré
 *
 * `planCreditNote` calcule le plan à chaque frappe. L'écran l'affiche —
 * montant, lignes, provision, sort de la facture — et n'envoie que ce plan.
 * Le serveur recalcule le plafond de son côté et peut encore refuser ; l'écran
 * n'est pas l'autorité, il est la lisibilité.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Ban,
  CalendarCheck,
  CheckCircle,
  FileText,
  Loader2,
  Mail,
  Tag,
  Wallet,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  canSubmit,
  isRefused,
  planCreditNote,
  requiresCancellationChoice,
  type CreditNoteMode,
  type CreditNoteReason,
  type TariffChange,
} from '../../../lib/creditNotes/plan';
import {
  billedDiffersFromCatalogue,
  normalizePriceType,
  PRICE_TYPE_LABELS,
  tariffOptions,
} from '../../../lib/creditNotes/tariffs';
import {
  emitCreditNote,
  envoyerNoteDeCredit,
  loadInvoiceDetail,
  MESSAGE_FAMILLE_MAX,
  type InvoiceDetail,
  type RegistrationDetail,
} from '../../../lib/creditNotes/gateway.supabase';
import { messageErreur } from '../../../lib/payments/erreur';
import { centimesSaisis } from '../../../lib/payments/montant';

const EUR = new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' });
const euros = (cents: number) => EUR.format(cents / 100);

/** Ce que chaque mode annonce, dans les mots de l'administration. */
const MODES: { mode: CreditNoteMode; titre: string; texte: string; reason: CreditNoteReason }[] = [
  {
    mode: 'tariff_correction',
    titre: 'Correction de tarif',
    texte:
      "Le parent avait droit au tarif réduit. L'écart est crédité et l'inscription est requalifiée. L'enfant vient au stage.",
    reason: 'tariff_correction',
  },
  {
    mode: 'per_registration',
    titre: 'Un ou plusieurs stages',
    texte: 'Crédite le montant des stages désignés, ni plus ni moins.',
    reason: 'cancellation',
  },
  {
    mode: 'full',
    titre: 'Toute la facture',
    texte: "Crédite tout ce que la facture réclame encore, notes de crédit antérieures déduites.",
    reason: 'cancellation',
  },
  {
    mode: 'custom',
    titre: 'Montant libre',
    texte: "Un montant que vous saisissez — jours d'absence, geste commercial.",
    reason: 'other',
  },
];

/** Le choix « annuler / conserver », sans valeur par défaut. */
type Devenir = 'cancel' | 'keep' | null;

interface Props {
  invoiceId: string | null;
  onClose: () => void;
  /** Appelé après une émission réussie, pour recharger la liste. */
  onEmitted: () => void;
}

export default function CreditNoteModal({ invoiceId, onClose, onEmitted }: Props) {
  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);

  const [mode, setMode] = useState<CreditNoteMode>('tariff_correction');
  const [selection, setSelection] = useState<string[]>([]);
  const [devenir, setDevenir] = useState<Devenir>(null);
  const [montantLibre, setMontantLibre] = useState('');
  const [tarifs, setTarifs] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState('');
  /*
    Envoyer la note à la famille : coché par défaut (décision d'Éclosion du
    9 octobre 2026). C'était le comportement d'avant le 4 septembre — l'ancien
    circuit envoyait d'office, sans choix — et c'est l'absence de cet envoi
    qui a laissé 34 notes réelles sans courriel. La case rend le choix ; elle
    ne le pose pas à chaque fois.
  */
  const [courrielDemande, setCourrielDemande] = useState(true);
  const [messageFamille, setMessageFamille] = useState('');
  const [etape, setEtape] = useState<'emission' | 'courriel' | null>(null);

  const reinitialiser = useCallback(() => {
    setMode('tariff_correction');
    setSelection([]);
    setDevenir(null);
    setMontantLibre('');
    setTarifs({});
    setNotes('');
    setCourrielDemande(true);
    setMessageFamille('');
    setErreur(null);
  }, []);

  useEffect(() => {
    if (!invoiceId) {
      setDetail(null);
      return;
    }
    let vivant = true;
    reinitialiser();
    setChargement(true);
    loadInvoiceDetail(invoiceId)
      .then((d) => {
        if (vivant) setDetail(d);
      })
      .catch((e) => {
        if (vivant) setErreur(messageErreur(e, 'Chargement de la facture impossible'));
      })
      .finally(() => {
        if (vivant) setChargement(false);
      });
    return () => {
      vivant = false;
    };
  }, [invoiceId, reinitialiser]);

  /** Les inscriptions qu'on peut encore créditer. */
  const creditables = useMemo(
    () =>
      (detail?.registrations ?? []).filter(
        (r) => !r.cancellationStatus.startsWith('cancelled_') && r.amountCents - r.alreadyCreditedCents > 0,
      ),
    [detail],
  );

  const changementsTarif = useMemo<TariffChange[]>(() => {
    const out: TariffChange[] = [];
    for (const [registrationId, priceTypeAfter] of Object.entries(tarifs)) {
      const r = creditables.find((x) => x.id === registrationId);
      if (!r || !priceTypeAfter) continue;
      const prix = r.tariffs[normalizePriceType(priceTypeAfter) ?? 'normal'];
      if (prix == null) continue;
      out.push({ registrationId, priceTypeAfter, amountAfterCents: prix });
    }
    return out;
  }, [tarifs, creditables]);

  /* Le mode « correction de tarif » ne propose pas l'annulation : l'enfant
     vient au stage. Le choix n'est donc ni posé ni requis. */
  const choixRequis = requiresCancellationChoice(mode);

  /* Sans adresse, la case n'a pas de sens : la note sera créée sans partir. */
  const adresse = detail?.familyEmail?.trim() ?? '';
  const courrielAEnvoyer = courrielDemande && adresse !== '';

  const plan = useMemo(() => {
    if (!detail) return null;
    /*
      La lecture du montant est partagée avec les cases de la répartition
      (`montant.ts`) : un `replace(',', '.')` local ne remplaçait que la
      PREMIÈRE virgule, et ne connaissait ni les espaces de milliers ni le
      symbole monétaire. Deux règles de lecture pour une même question, ce
      sont deux comportements à expliquer.
    */
    const centimes = centimesSaisis(montantLibre);
    return planCreditNote({
      mode,
      reason: MODES.find((m) => m.mode === mode)!.reason,
      invoice: detail.invoice,
      registrations: detail.registrations,
      selectedRegistrationIds:
        mode === 'tariff_correction' ? changementsTarif.map((c) => c.registrationId) : selection,
      customAmountCents: centimes ?? 0,
      tariffChanges: changementsTarif,
      cancelRegistrations: choixRequis ? devenir === 'cancel' : false,
    });
  }, [detail, mode, selection, montantLibre, changementsTarif, devenir, choixRequis]);

  const planValide = plan && !isRefused(plan) ? plan : null;
  const refus = plan && isRefused(plan) ? plan.refusals : [];
  const peutEnvoyer = canSubmit({
    mode,
    outcome: plan,
    cancelChoice: devenir === null ? null : devenir === 'cancel',
    submitting: envoi,
  });

  const basculer = (id: string) => {
    setSelection((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  /**
   * Répondre au choix « annuler / conserver ».
   *
   * En mode « toute la facture », répondre « annuler » désigne **toutes** les
   * inscriptions créditables. Sans cela, le geste le plus naturel — créditer
   * toute la facture et annuler les stages — ne cancellait rien : la sélection
   * y est facultative, donc vide par défaut, et les lignes d'annulation
   * n'existaient pas. Ce n'est pas une présomption : c'est ce que la réponse
   * veut dire. La liste reste modifiable ensuite, et le récapitulatif montre
   * ligne par ligne ce qui sera annulé.
   */
  const repondre = (choix: Devenir) => {
    setDevenir(choix);
    if (mode === 'full' && choix === 'cancel') {
      setSelection(creditables.map((r) => r.id));
    }
  };

  const changerMode = (m: CreditNoteMode) => {
    setMode(m);
    // Le choix « annuler / conserver » est REPOSÉ à chaque changement de mode :
    // un choix fait pour « toute la facture » ne vaut pas pour un stage précis.
    setDevenir(null);
    setSelection([]);
    setTarifs({});
  };

  const envoyer = async () => {
    if (!invoiceId || !planValide) return;
    setEnvoi(true);
    setEtape('emission');
    setErreur(null);
    try {
      const r = await emitCreditNote(invoiceId, planValide, notes);
      const morceaux = [`Note de crédit ${r.creditNoteNumber} — ${euros(r.amountCents)}`];
      if (r.registrationsRequalified > 0) morceaux.push(`${r.registrationsRequalified} inscription(s) requalifiée(s)`);
      if (r.registrationsCancelled > 0) morceaux.push(`${r.registrationsCancelled} inscription(s) annulée(s)`);
      if (r.provisionCents > 0) morceaux.push(`${euros(r.provisionCents)} en provision`);
      toast.success(morceaux.join(' · '), { duration: 8000 });

      /*
        Le courriel APRÈS la note, jamais dans la même opération. La note est
        écrite et compte déjà dans les soldes : un envoi raté ne l'annule pas,
        et ne doit pas se lire comme une émission ratée — on réémettrait la
        note. On le dit, et elle se renvoie depuis le détail de la facture.

        La fonction d'envoi régénère le PDF elle-même : si la production
        ci-dessus a échoué et que l'envoi passe, le PDF existe désormais, et
        l'avertissement sur son absence n'a plus lieu d'être.
      */
      let pdfProduitParLEnvoi = false;
      if (courrielAEnvoyer) {
        setEtape('courriel');
        const c = r.creditNoteId
          ? await envoyerNoteDeCredit(r.creditNoteId, messageFamille)
          : { envoyeA: null, erreur: 'l’identifiant de la note n’est pas revenu' };
        if (c.envoyeA) {
          pdfProduitParLEnvoi = true;
          toast.success(`${r.creditNoteNumber} envoyée à ${c.envoyeA}.`, { duration: 8000 });
        } else {
          toast(
            `La note de crédit ${r.creditNoteNumber} est enregistrée, mais le courriel n'est pas parti (${c.erreur}). Renvoyez-la depuis le détail de la facture.`,
            { duration: 15000, icon: '⚠️' },
          );
        }
      }

      /*
        La base replafonne la provision par l'excès réel. L'aperçu fait le même
        calcul, donc les deux doivent concorder — et si un jour ils divergent,
        c'est la base qui a raison et il faut le voir plutôt que de croire
        l'aperçu.
      */
      if (r.pdfError && !pdfProduitParLEnvoi) {
        // La note existe : le dire, pour que l'administration ne la reemette pas.
        toast(
          `La note de crédit ${r.creditNoteNumber} est enregistrée, mais son PDF n'a pas pu être produit (${r.pdfError}). Elle compte déjà dans les soldes.`,
          { duration: 12000, icon: '⚠️' },
        );
      }
      if (r.excessCents > r.provisionCents) {
        toast(
          `${euros(r.excessCents - r.provisionCents)} restent affectés à cette facture alors qu'elle ne les réclame plus.`,
          { duration: 10000, icon: '⚠️' },
        );
      }
      onEmitted();
      onClose();
    } catch (e) {
      // Le message vient de la base — « la facture X ne peut plus être créditée
      // que de Y » — et il est plus utile que n'importe quelle reformulation.
      setErreur(messageErreur(e, 'Émission impossible'));
    } finally {
      setEnvoi(false);
      setEtape(null);
    }
  };

  return (
    <Dialog
      open={!!invoiceId}
      onClose={() => !envoi && onClose()}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/40" aria-hidden="true" />

        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full p-6 max-h-[92vh] overflow-y-auto">
          <Dialog.Title className="text-lg font-semibold flex items-center mb-4">
            <FileText className="h-5 w-5 text-primary-600 mr-2" />
            Émettre une note de crédit
          </Dialog.Title>

          {chargement && (
            <div className="flex items-center text-gray-600 py-10 justify-center">
              <Loader2 className="animate-spin h-5 w-5 mr-2" />
              Chargement de la facture…
            </div>
          )}

          {!chargement && erreur && !detail && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800 flex">
              <AlertCircle className="h-5 w-5 mr-2 shrink-0" />
              {erreur}
            </div>
          )}

          {!chargement && detail && (
            <div className="space-y-6">
              {/* --- Ce que la facture réclame encore ------------------- */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Facture</p>
                    <p className="font-medium">{detail.invoice.invoiceNumber}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Famille</p>
                    <p className="font-medium">{detail.familyName}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Montant</p>
                    <p className="font-medium">{euros(detail.invoice.amountCents)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Reste créditable</p>
                    <p className="font-medium">
                      {euros(Math.max(0, detail.invoice.amountCents - detail.invoice.creditNotesCents))}
                    </p>
                  </div>
                </div>
                {detail.invoice.creditNotesCents > 0 && (
                  <p className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex">
                    <AlertTriangle className="h-4 w-4 mr-2 mt-0.5 shrink-0" />
                    {euros(detail.invoice.creditNotesCents)} ont déjà été crédités sur cette facture. Une
                    nouvelle note s'applique au restant dû, elle ne le double pas.
                  </p>
                )}
                {detail.invoice.allocatedCents > 0 && (
                  <p className="mt-2 text-sm text-gray-600 flex">
                    <Wallet className="h-4 w-4 mr-2 mt-0.5 shrink-0" />
                    {euros(detail.invoice.allocatedCents)} ont été reçus sur cette facture.
                  </p>
                )}
                {detail.orphanCount > 0 && (
                  <p className="mt-2 text-sm text-gray-600">
                    {detail.orphanCount} inscription(s) sont liées à cette facture par leur numéro
                    seulement, pas par le tableau de la facture. Elles sont incluses.
                  </p>
                )}
              </div>

              {/* --- Le mode ------------------------------------------- */}
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Pourquoi cette note de crédit&nbsp;?</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {MODES.map((m) => (
                    <button
                      key={m.mode}
                      type="button"
                      onClick={() => changerMode(m.mode)}
                      className={clsx(
                        'text-left border rounded-lg p-3 transition',
                        mode === m.mode
                          ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                          : 'border-gray-200 hover:border-gray-300',
                      )}
                    >
                      <span className="flex items-center justify-between font-medium">
                        {m.titre}
                        {mode === m.mode && <CheckCircle className="h-4 w-4 text-primary-600" />}
                      </span>
                      <span className="block text-sm text-gray-600 mt-1">{m.texte}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* --- Correction de tarif ------------------------------- */}
              {mode === 'tariff_correction' && (
                <div>
                  <h3 className="font-medium text-gray-700 mb-2">Le tarif juste, par inscription</h3>
                  {creditables.length === 0 && (
                    <p className="text-sm text-gray-600">
                      Aucune inscription créditable sur cette facture.
                    </p>
                  )}
                  <div className="space-y-3">
                    {creditables.map((r) => (
                      <LigneTarif
                        key={r.id}
                        registration={r}
                        choisi={tarifs[r.id] ?? null}
                        onChoisir={(pt) =>
                          setTarifs((t) => {
                            const suite = { ...t };
                            if (pt === null) delete suite[r.id];
                            else suite[r.id] = pt;
                            return suite;
                          })
                        }
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* --- Désignation des stages ---------------------------- */}
              {(mode === 'per_registration' || mode === 'custom' || mode === 'full') && (
                <div>
                  <h3 className="font-medium text-gray-700 mb-2">
                    {mode === 'per_registration'
                      ? 'Quels stages créditer ?'
                      : 'À quels stages rattacher cette note de crédit ? (facultatif)'}
                  </h3>
                  <div className="space-y-2">
                    {creditables.map((r) => {
                      const choisi = selection.includes(r.id);
                      return (
                        <button
                          key={r.id}
                          type="button"
                          onClick={() => basculer(r.id)}
                          className={clsx(
                            'w-full text-left border rounded-lg p-3 flex justify-between items-start transition',
                            choisi
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300',
                          )}
                        >
                          <span>
                            <span className="block font-medium">{r.stageTitle}</span>
                            <span className="block text-sm text-gray-600">
                              {r.kidName} · {r.startDate} → {r.endDate}
                              {r.centerName ? ` · ${r.centerName}` : ''}
                            </span>
                            {r.cancellationStatus === 'requested' && (
                              <span className="inline-block text-xs bg-orange-100 text-orange-800 px-2 py-0.5 rounded mt-1">
                                Annulation demandée par le parent
                              </span>
                            )}
                            {r.alreadyCreditedCents > 0 && (
                              <span className="block text-xs text-amber-700 mt-1">
                                {euros(r.alreadyCreditedCents)} déjà crédités
                              </span>
                            )}
                          </span>
                          <span className="text-right shrink-0 ml-3">
                            <span className="block font-medium">{euros(r.amountCents)}</span>
                            {choisi && <CheckCircle className="h-4 w-4 text-primary-600 ml-auto mt-1" />}
                          </span>
                        </button>
                      );
                    })}
                    {creditables.length === 0 && (
                      <p className="text-sm text-gray-600">
                        Aucune inscription créditable — la note portera sur la facture seule.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* --- Montant libre ------------------------------------- */}
              {mode === 'custom' && (
                <div>
                  <h3 className="font-medium text-gray-700 mb-2">Montant à créditer</h3>
                  <div className="flex items-center">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={montantLibre}
                      onChange={(e) => setMontantLibre(e.target.value)}
                      placeholder="0,00"
                      className="form-input w-40"
                    />
                    <span className="ml-2">€</span>
                  </div>
                </div>
              )}

              {/* --- LE choix, sans présélection ----------------------- */}
              {choixRequis && (
                <div
                  className={clsx(
                    'rounded-lg border p-4',
                    devenir === null ? 'border-amber-300 bg-amber-50' : 'border-gray-200',
                  )}
                >
                  <h3 className="font-medium text-gray-800">
                    Que deviennent les inscriptions&nbsp;?
                  </h3>
                  <p className="text-sm text-gray-600 mt-1 mb-3">
                    {devenir === null
                      ? 'Aucune réponse par défaut : ce choix libère ou non des places, il doit être fait.'
                      : devenir === 'cancel'
                        ? 'Les inscriptions désignées seront annulées et leurs places libérées.'
                        : 'Les inscriptions restent actives : les enfants viennent au stage.'}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => repondre('cancel')}
                      className={clsx(
                        'border rounded-lg p-3 text-left flex items-start transition',
                        devenir === 'cancel'
                          ? 'border-red-500 bg-red-50 ring-1 ring-red-500'
                          : 'border-gray-300 bg-white hover:border-gray-400',
                      )}
                    >
                      <Ban className="h-5 w-5 text-red-600 mr-2 shrink-0" />
                      <span>
                        <span className="block font-medium">Annuler les inscriptions</span>
                        <span className="block text-sm text-gray-600">
                          L'enfant ne vient pas. La place est rendue.
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => repondre('keep')}
                      className={clsx(
                        'border rounded-lg p-3 text-left flex items-start transition',
                        devenir === 'keep'
                          ? 'border-green-600 bg-green-50 ring-1 ring-green-600'
                          : 'border-gray-300 bg-white hover:border-gray-400',
                      )}
                    >
                      <CalendarCheck className="h-5 w-5 text-green-700 mr-2 shrink-0" />
                      <span>
                        <span className="block font-medium">Conserver les inscriptions</span>
                        <span className="block text-sm text-gray-600">
                          L'enfant vient. Seul l'argent bouge.
                        </span>
                      </span>
                    </button>
                  </div>
                </div>
              )}

              {/* --- Note administrative ------------------------------ */}
              <div>
                <h3 className="font-medium text-gray-700 mb-2">Note administrative</h3>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="form-input w-full"
                  placeholder="Pourquoi cette note de crédit (facultatif, conservé sur la note)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Interne : enregistrée sur la note de crédit, elle n'est pas envoyée à la famille.
                </p>
              </div>

              {/* --- Envoi à la famille ------------------------------- */}
              {/*
                Deux champs de texte se suivent ici, et ils ne vont pas au même
                endroit : la note administrative reste dans l'outil, le message
                part à la famille. Chacun le dit sous son titre.
              */}
              <div className="rounded-lg border border-gray-200 p-4">
                <h3 className="font-medium text-gray-700 flex items-center">
                  <Mail className="h-4 w-4 mr-2 text-primary-600" />
                  Envoi à la famille
                </h3>
                {adresse ? (
                  <label className="mt-2 flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={courrielDemande}
                      onChange={(e) => setCourrielDemande(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>
                      Envoyer la note de crédit et son PDF à <strong>{adresse}</strong>
                    </span>
                  </label>
                ) : (
                  <p className="mt-2 text-sm text-amber-800">
                    Cette famille n'a pas d'adresse courriel : la note sera créée sans être envoyée.
                  </p>
                )}
                {courrielAEnvoyer && (
                  <div className="mt-3">
                    <label htmlFor="message-famille" className="block text-sm text-gray-700 mb-1">
                      Message à la famille (facultatif)
                    </label>
                    <textarea
                      id="message-famille"
                      value={messageFamille}
                      onChange={(e) => setMessageFamille(e.target.value)}
                      rows={3}
                      maxLength={MESSAGE_FAMILLE_MAX}
                      className="form-input w-full"
                      placeholder="Ajouté au courriel, au-dessus du détail de la note."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Visible par la famille.
                      {messageFamille.length > MESSAGE_FAMILLE_MAX - 200 &&
                        ` ${messageFamille.length} / ${MESSAGE_FAMILLE_MAX} caractères.`}
                    </p>
                  </div>
                )}
              </div>

              {/* --- Le plan, avant d'écrire -------------------------- */}
              {refus.length > 0 && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800">
                  <p className="font-medium flex items-center mb-1">
                    <AlertCircle className="h-4 w-4 mr-2" /> Rien ne sera écrit
                  </p>
                  <ul className="list-disc ml-6 space-y-1">
                    {refus.map((r, i) => (
                      <li key={i}>{r}</li>
                    ))}
                  </ul>
                </div>
              )}

              {planValide && (
                <div className="rounded-lg border border-primary-200 bg-primary-50/60 p-4">
                  <p className="font-medium text-gray-800 mb-2">Ce qui sera écrit</p>
                  <dl className="grid grid-cols-2 gap-y-1 text-sm">
                    <dt className="text-gray-600">Note de crédit</dt>
                    <dd className="font-medium text-right">{euros(planValide.amountCents)}</dd>
                    <dt className="text-gray-600">La facture réclamera</dt>
                    <dd className="font-medium text-right">
                      {euros(planValide.remainingNetAfterCents)}
                      {planValide.invoiceBecomesCancelled && ' — annulée'}
                    </dd>
                    {planValide.provisionCents > 0 && (
                      <>
                        <dt className="text-gray-600">En provision de la famille</dt>
                        <dd className="font-medium text-right">{euros(planValide.provisionCents)}</dd>
                      </>
                    )}
                    <dt className="text-gray-600">Courriel à la famille</dt>
                    <dd className="font-medium text-right break-all">
                      {courrielAEnvoyer ? adresse : 'aucun'}
                    </dd>
                  </dl>

                  {planValide.lines.length > 0 && (
                    <ul className="mt-3 space-y-1 text-sm">
                      {planValide.lines.map((l) => (
                        <li key={l.registrationId} className="flex items-start justify-between">
                          <span className="flex items-center">
                            {l.registrationCancelled ? (
                              <Ban className="h-3.5 w-3.5 text-red-600 mr-1.5 shrink-0" />
                            ) : l.priceTypeAfter ? (
                              <Tag className="h-3.5 w-3.5 text-primary-600 mr-1.5 shrink-0" />
                            ) : (
                              <CalendarCheck className="h-3.5 w-3.5 text-green-700 mr-1.5 shrink-0" />
                            )}
                            <span>
                              {l.label}
                              {l.priceTypeAfter && (
                                <span className="text-gray-600">
                                  {' '}
                                  — {PRICE_TYPE_LABELS[normalizePriceType(l.priceTypeBefore) ?? 'normal']}{' '}
                                  <ArrowRight className="h-3 w-3 inline" />{' '}
                                  {PRICE_TYPE_LABELS[normalizePriceType(l.priceTypeAfter) ?? 'normal']}
                                </span>
                              )}
                              {l.registrationCancelled && (
                                <span className="text-red-700"> — annulée</span>
                              )}
                            </span>
                          </span>
                          <span className="font-medium shrink-0 ml-3">{euros(l.amountCents)}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {planValide.warnings.length > 0 && (
                    <ul className="mt-3 space-y-1 text-sm text-amber-800">
                      {planValide.warnings.map((w, i) => (
                        <li key={i} className="flex">
                          <AlertTriangle className="h-4 w-4 mr-2 mt-0.5 shrink-0" />
                          {w}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {erreur && detail && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800 flex">
                  <AlertCircle className="h-5 w-5 mr-2 shrink-0" />
                  {erreur}
                </div>
              )}

              <div className="flex justify-end items-center gap-3 pt-4 border-t">
                {choixRequis && devenir === null && (
                  <span className="text-sm text-amber-800 mr-auto">
                    Répondez à « Que deviennent les inscriptions ? » pour continuer.
                  </span>
                )}
                <button type="button" onClick={onClose} disabled={envoi} className="btn-outline">
                  Fermer
                </button>
                <button
                  type="button"
                  onClick={envoyer}
                  disabled={!peutEnvoyer}
                  className={clsx('btn-primary', !peutEnvoyer && 'opacity-50 cursor-not-allowed')}
                >
                  {envoi ? (
                    <span className="flex items-center">
                      <Loader2 className="animate-spin h-4 w-4 mr-2" />
                      {etape === 'courriel' ? 'Envoi du courriel…' : 'Émission…'}
                    </span>
                  ) : courrielAEnvoyer ? (
                    'Créer et envoyer la note de crédit'
                  ) : (
                    'Créer la note de crédit'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Une inscription et les tarifs qu'on peut lui appliquer.
 *
 * Les options viennent des prix réels de la session, et l'écart se calcule
 * depuis le montant facturé — jamais depuis le catalogue. Sur 204 inscriptions
 * en production, les deux diffèrent ; l'écran le dit quand c'est le cas.
 */
function LigneTarif({
  registration,
  choisi,
  onChoisir,
}: {
  registration: RegistrationDetail;
  choisi: string | null;
  onChoisir: (priceType: string | null) => void;
}) {
  const options = tariffOptions(registration.amountCents, registration.priceType, registration.tariffs);
  const ecart = billedDiffersFromCatalogue(
    registration.amountCents,
    registration.priceType,
    registration.tariffs,
  );
  const courant = normalizePriceType(registration.priceType);

  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <div className="flex justify-between items-start">
        <div>
          <p className="font-medium">{registration.stageTitle}</p>
          <p className="text-sm text-gray-600">
            {registration.kidName} · {registration.startDate} → {registration.endDate}
          </p>
          <p className="text-sm text-gray-600">
            Facturé {euros(registration.amountCents)}
            {courant ? ` au ${PRICE_TYPE_LABELS[courant].toLowerCase()}` : ''}
          </p>
          {ecart.differs && ecart.catalogueCents !== null && (
            <p className="text-xs text-amber-700 mt-1">
              Le catalogue de la session dit {euros(ecart.catalogueCents)} pour ce tarif. L'écart part du
              montant facturé.
            </p>
          )}
        </div>
        {choisi && (
          <button
            type="button"
            onClick={() => onChoisir(null)}
            className="text-sm text-gray-500 hover:text-gray-700 underline shrink-0 ml-3"
          >
            ne pas corriger
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        {options
          .filter((o) => !o.current)
          .map((o) => (
            <button
              key={o.priceType}
              type="button"
              onClick={() => onChoisir(o.priceType)}
              className={clsx(
                'border rounded-md px-3 py-1.5 text-sm text-left transition',
                choisi === o.priceType
                  ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                  : 'border-gray-300 hover:border-gray-400',
              )}
            >
              <span className="font-medium">{o.label}</span>
              <span className="block text-xs text-gray-600">
                {euros(o.amountCents)} · crédite {euros(o.creditCents)}
              </span>
            </button>
          ))}
        {options.filter((o) => !o.current).length === 0 && (
          <p className="text-sm text-gray-600">
            Aucun tarif inférieur n'est disponible sur cette session.
          </p>
        )}
      </div>
    </div>
  );
}
